/**
 * VÉRAULT Portal — Content Studio · Social
 *
 * Drafts-only Social Post Creator (v1):
 *   · List of social-post cards (caption preview, platform chips, source-journal link)
 *   · Filters: status tabs (All / Drafts / Scheduled) + platform pills
 *   · "New social post" → editor modal
 *   · Repurpose flow lives on the Journal list page — landing here as fresh drafts
 *   · No external publish APIs at this stage; "Scheduled" is a tracking date only
 *
 * Backed by /api/cms/social{,/:id} (Phase 4 backend).
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Share2,
  Edit3,
  Trash2,
  Calendar as CalendarIcon,
  Instagram,
  Linkedin,
  Mail,
  Twitter,
  Facebook,
  Hash,
  ExternalLink,
  X,
  RotateCw,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import {
  Surface,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
} from "../../../index";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram, limit: 2200 },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, limit: 3000 },
  { id: "email", label: "Email", icon: Mail, limit: 1200 },
  { id: "facebook", label: "Facebook", icon: Facebook, limit: 2200 },
  { id: "twitter", label: "X", icon: Twitter, limit: 280 },
  { id: "threads", label: "Threads", icon: Hash, limit: 500 },
];
const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

const STATUS_FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "draft", label: "Drafts", match: (p) => p.status === "draft" },
  { id: "scheduled", label: "Scheduled", match: (p) => p.status === "scheduled" },
  { id: "published", label: "Published", match: (p) => p.status === "published" },
  { id: "failed", label: "Failed", match: (p) => p.status === "failed" },
];

// Visual treatment per lifecycle state (Wave 5). Failed is intentionally the
// only state that breaks neutral palette — it should catch the eye.
const STATUS_TONE = {
  draft: "neutral",
  scheduled: "gold",
  publishing: "gold",
  published: "neutral",
  failed: "terracotta",
};

function PlatformChip({ id }) {
  const meta = PLATFORM_MAP[id];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[hsl(var(--background-stone))]/70 border border-[rgba(0,0,0,0.06)] font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-secondary))]"
      data-testid={`social-chip-${id}`}
    >
      <Icon className="size-2.5" strokeWidth={1.6} />
      {meta.label}
    </span>
  );
}

function PostCard({ post, onEdit, onDelete, onRetry }) {
  const isFailed = post.status === "failed";
  const isPublished = post.status === "published";
  return (
    <Surface
      variant="default"
      padded={false}
      className={`overflow-hidden ${
        isFailed ? "ring-1 ring-[hsl(var(--accent-terracotta))]/30" : ""
      }`}
      data-testid={`social-card-${post.id}`}
    >
      <div className="p-5 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusTag tone={STATUS_TONE[post.status] || "neutral"}>
            {post.status}
          </StatusTag>
          {post.scheduled_for && !isPublished && !isFailed && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">
              <CalendarIcon className="size-2.5" strokeWidth={1.5} />
              {fmt.formatDate(post.scheduled_for)}
            </span>
          )}
          {isPublished && post.published_at && (
            <span
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-secondary))]"
              data-testid={`social-published-at-${post.id}`}
            >
              <CheckCircle2 className="size-2.5" strokeWidth={1.5} />
              {fmt.formatDate(post.published_at)}
            </span>
          )}
          {post.destination_tag && (
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              #{post.destination_tag}
            </span>
          )}
          {(post.retry_count || 0) > 0 && (
            <span
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
              title={`Retried ${post.retry_count}× `}
              data-testid={`social-retry-count-${post.id}`}
            >
              ×{post.retry_count}
            </span>
          )}
        </div>
        <p className="font-body text-[14px] text-[hsl(var(--foreground))] whitespace-pre-line line-clamp-5 leading-relaxed">
          {post.caption}
        </p>
        {isFailed && post.last_error && (
          <div
            className="flex items-start gap-2 px-3 py-2 rounded bg-[hsl(var(--accent-terracotta))]/8 border border-[hsl(var(--accent-terracotta))]/20"
            data-testid={`social-error-${post.id}`}
          >
            <AlertCircle
              className="size-3.5 mt-0.5 text-[hsl(var(--accent-terracotta))] flex-shrink-0"
              strokeWidth={1.5}
            />
            <p className="font-body text-[12.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
              {post.last_error}
            </p>
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          {(post.platforms || []).map((p) => (
            <PlatformChip key={p} id={p} />
          ))}
        </div>
        {post.source_journal_id && (
          <div className="pt-1">
            <Link
              to={`/advisor/content/journal/${post.source_journal_id}`}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors"
              data-testid={`social-source-${post.id}`}
            >
              <ExternalLink className="size-2.5" strokeWidth={1.5} />
              Repurposed from: {post.source_journal_title || "journal"}
            </Link>
          </div>
        )}
      </div>
      <div className="border-t border-[rgba(0,0,0,0.05)] px-5 py-2.5 flex items-center justify-between">
        <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">
          Edited {fmt.formatDate(post.updated_at)}
        </span>
        <div className="flex items-center gap-1">
          {isFailed && (
            <button
              onClick={() => onRetry(post)}
              data-testid={`social-retry-${post.id}`}
              className="h-8 px-2.5 inline-flex items-center gap-1 rounded font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))]/10 transition-colors"
              title="Retry publishing"
            >
              <RotateCw className="size-3" strokeWidth={1.6} />
              Retry
            </button>
          )}
          <button
            onClick={() => onEdit(post)}
            data-testid={`social-edit-${post.id}`}
            className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
            title="Edit"
          >
            <Edit3 className="size-3.5" strokeWidth={1.5} />
          </button>
          <button
            onClick={() => onDelete(post)}
            data-testid={`social-delete-${post.id}`}
            className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-paper))]"
            title="Delete"
          >
            <Trash2 className="size-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </Surface>
  );
}

export function SocialPostEditor({ open, initial, onClose, onSaved }) {
  const empty = {
    caption: "",
    platforms: ["instagram"],
    tags: "",
    destination_tag: "",
    scheduled_for: "",
    source_journal_id: "",
    source_journal_title: "",
    status: "draft",
  };
  const [data, setData] = React.useState(empty);
  const [saving, setSaving] = React.useState(false);
  const isEdit = !!initial?.id;

  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      setData({
        caption: initial.caption || "",
        platforms: initial.platforms?.length ? initial.platforms : ["instagram"],
        tags: (initial.tags || []).join(", "),
        destination_tag: initial.destination_tag || "",
        scheduled_for: initial.scheduled_for ? initial.scheduled_for.slice(0, 16) : "",
        source_journal_id: initial.source_journal_id || "",
        source_journal_title: initial.source_journal_title || "",
        status: initial.status || "draft",
      });
    } else {
      setData(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  if (!open) return null;

  const togglePlatform = (id) => {
    setData((d) => {
      const next = d.platforms.includes(id)
        ? d.platforms.filter((x) => x !== id)
        : [...d.platforms, id];
      return { ...d, platforms: next };
    });
  };

  const handleSave = async () => {
    if (!data.caption.trim()) {
      window.alert("Caption is required.");
      return;
    }
    if (!data.platforms.length) {
      window.alert("Choose at least one platform.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        caption: data.caption.trim(),
        platforms: data.platforms,
        tags: data.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        destination_tag: data.destination_tag.trim(),
        source_journal_id: data.source_journal_id,
        source_journal_title: data.source_journal_title,
      };
      if (data.scheduled_for) {
        // datetime-local has no TZ — store as-is (treat as user local intent)
        payload.scheduled_for = new Date(data.scheduled_for).toISOString();
      } else {
        payload.scheduled_for = "";
      }
      let result;
      if (isEdit) {
        const r = await api.put(`/cms/social/${initial.id}`, payload);
        result = r.data;
      } else {
        const r = await api.post("/cms/social", payload);
        result = r.data;
      }
      onSaved(result);
      onClose();
    } catch (e) {
      window.alert(
        `Save failed: ${e?.response?.data?.detail || e.message || "unknown error"}`,
      );
    } finally {
      setSaving(false);
    }
  };

  // Length warnings per selected platform
  const chars = data.caption.length;
  const warnings = data.platforms
    .map((p) => PLATFORM_MAP[p])
    .filter(Boolean)
    .filter((p) => chars > p.limit)
    .map((p) => `${p.label} (${p.limit} max)`);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="social-editor"
    >
      <div
        className="w-full max-w-2xl bg-[hsl(var(--background))] rounded-[var(--radius)] shadow-2xl border border-[rgba(0,0,0,0.08)] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(0,0,0,0.06)]">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {isEdit ? "Edit social post" : "New social post"}
            </p>
            <p className="mt-0.5 font-serif text-lg">
              {data.source_journal_title
                ? `From "${data.source_journal_title}"`
                : "A caption, calmly composed."}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="social-editor-close"
            className="size-9 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Platforms
            </label>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="social-editor-platforms">
              {PLATFORMS.map(({ id, label, icon: Icon }) => {
                const active = data.platforms.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => togglePlatform(id)}
                    data-testid={`social-editor-platform-${id}`}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full font-body text-[12.5px] tracking-tight border transition-colors duration-200 ${
                      active
                        ? "bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] border-transparent"
                        : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-secondary))] border-[rgba(0,0,0,0.08)] hover:text-[hsl(var(--foreground))]"
                    }`}
                  >
                    <Icon className="size-3" strokeWidth={1.5} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Caption
              </label>
              <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">
                {chars} chars
                {warnings.length > 0 && (
                  <span className="ml-2 text-[hsl(var(--accent-terracotta))]">
                    over: {warnings.join(", ")}
                  </span>
                )}
              </span>
            </div>
            <textarea
              value={data.caption}
              onChange={(e) => setData({ ...data, caption: e.target.value })}
              rows={8}
              placeholder="Write a caption. Use line breaks for rhythm."
              data-testid="social-editor-caption"
              className="mt-1.5 w-full px-3 py-2.5 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))] font-body leading-relaxed"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Destination tag
              </label>
              <input
                type="text"
                value={data.destination_tag}
                onChange={(e) => setData({ ...data, destination_tag: e.target.value })}
                placeholder="e.g. italy"
                data-testid="social-editor-destination"
                className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={data.tags}
                onChange={(e) => setData({ ...data, tags: e.target.value })}
                placeholder="editorial, slow-travel"
                data-testid="social-editor-tags"
                className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Schedule date <span className="opacity-60">(optional · tracking only)</span>
            </label>
            <input
              type="datetime-local"
              value={data.scheduled_for}
              onChange={(e) => setData({ ...data, scheduled_for: e.target.value })}
              data-testid="social-editor-schedule"
              className="mt-1.5 w-full sm:w-72 h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[rgba(0,0,0,0.06)]">
          <PortalButton variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </PortalButton>
          <PortalButton
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="social-editor-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Save draft"}
          </PortalButton>
        </div>
      </div>
    </div>
  );
}

export default function ContentSocial() {
  const [posts, setPosts] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [platformFilter, setPlatformFilter] = React.useState("");
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const refresh = React.useCallback(() => {
    setPosts(null);
    api
      .get("/cms/social")
      .then(({ data }) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setError("Couldn't load social posts."));
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = React.useMemo(() => {
    if (!posts) return null;
    const sf = STATUS_FILTERS.find((s) => s.id === statusFilter) || STATUS_FILTERS[0];
    return posts.filter((p) => {
      if (!sf.match(p)) return false;
      if (platformFilter && !(p.platforms || []).includes(platformFilter)) return false;
      return true;
    });
  }, [posts, statusFilter, platformFilter]);

  const handleNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };
  const handleEdit = (post) => {
    setEditing(post);
    setEditorOpen(true);
  };
  const handleDelete = async (post) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await api.delete(`/cms/social/${post.id}`);
    refresh();
  };
  const handleRetry = async (post) => {
    try {
      await api.post(`/cms/social/${post.id}/retry`);
      refresh();
    } catch (e) {
      window.alert(
        `Retry failed: ${e?.response?.data?.detail || e.message || "unknown error"}`,
      );
    }
  };
  const handleSaved = () => {
    refresh();
  };

  return (
    <div className="space-y-7" data-testid="content-social">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <nav className="flex flex-wrap gap-1" data-testid="social-status-filters">
          {STATUS_FILTERS.map((f) => {
            const count = posts ? posts.filter(f.match).length : null;
            return (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                data-testid={`social-filter-${f.id}`}
                className={`relative px-3 py-1.5 font-body text-[13px] tracking-tight transition-colors duration-200 inline-flex items-center gap-1.5 ${
                  statusFilter === f.id
                    ? "text-[hsl(var(--foreground))]"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                <span className={statusFilter === f.id ? "font-serif" : ""}>{f.label}</span>
                {count !== null && count > 0 && (
                  <span
                    className={`font-mono text-[10px] tabular-nums ${
                      statusFilter === f.id
                        ? "text-[hsl(var(--accent-gold))]"
                        : "text-[hsl(var(--foreground-muted))]/70"
                    }`}
                  >
                    {count}
                  </span>
                )}
                <span
                  className={`absolute -bottom-px left-3 right-3 h-px ${
                    statusFilter === f.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                  }`}
                  aria-hidden
                />
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1" data-testid="social-platform-filters">
            <button
              onClick={() => setPlatformFilter("")}
              data-testid="social-platform-all"
              className={`px-2.5 h-8 rounded-full text-[12px] tracking-tight transition-colors ${
                !platformFilter
                  ? "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground))] font-serif"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              All platforms
            </button>
            {PLATFORMS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setPlatformFilter(platformFilter === id ? "" : id)}
                data-testid={`social-platform-${id}`}
                className={`inline-flex items-center gap-1 px-2 h-8 rounded-full text-[12px] tracking-tight transition-colors ${
                  platformFilter === id
                    ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                <Icon className="size-3" strokeWidth={1.5} />
                {label}
              </button>
            ))}
          </div>
          <PortalButton
            variant="primary"
            size="md"
            icon={<Plus className="size-3.5" strokeWidth={1.5} />}
            onClick={handleNew}
            data-testid="social-new-post"
          >
            New social post
          </PortalButton>
        </div>
      </div>

      <div>
        <EditorialRule
          label={
            filtered === null ? "loading" : `${filtered.length} post${filtered.length === 1 ? "" : "s"}`
          }
        />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<Share2 className="size-6" strokeWidth={1.25} />} />
          ) : filtered === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Empty
              title="No social posts yet"
              hint="Begin a new draft, or repurpose a journal entry from The editorial archive."
              icon={<Share2 className="size-6" strokeWidth={1.25} />}
              action={
                <div className="flex gap-2">
                  <PortalButton size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={handleNew}>
                    New draft
                  </PortalButton>
                  <PortalButton as={Link} to="/portal/advisor/content/journal" size="sm" variant="ghost">
                    Open Journal
                  </PortalButton>
                </div>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="social-grid">
              {filtered.map((p) => (
                <PostCard key={p.id} post={p} onEdit={handleEdit} onDelete={handleDelete} onRetry={handleRetry} />
              ))}
            </div>
          )}
        </div>
      </div>

      <SocialPostEditor
        open={editorOpen}
        initial={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
